import fetchApi from '../util/fetchApi';
import checkErrors from './checkErrors';
import checkDocumentResponse, { STATUS } from './checkDocumentResponse';
import { DetailedUWEEntity, WFAccessToken } from './types';

type UnknownObject = {
  [key: string]: unknown;
};

const getAccessTokenHeaders = (wfAccessToken: WFAccessToken) => {
  // Guard on the hash, not on the object: wfAccessToken defaults to `{}` outside
  // a workflow step, and `{}` is truthy — that sent `Workflow-Token: undefined`,
  // which the fork reads as a real (invalid) hash instead of treating the
  // request as unauthenticated-by-token.
  if (!wfAccessToken?.hash) return {};

  return {
    headers: {
      'Workflow-Token': wfAccessToken.hash,
    }
  };
};

// STILL TODO: TS => add return types for all functions
const UWEEntityApi = {
  async getWorkTray({
    resource,
    offset,
    size,
    sortBy,
    ...filters
  }: {
    resource: string;
    offset: number;
    size: number;
    sortBy: string;
  } & UnknownObject) {
    const result = await checkErrors(await fetchApi.api.get(resource, { query: { offset, size, sortBy, ...filters } }));

    return result.data ? result.data : result;
  },

  async getOpenCases() {
    return checkErrors(await fetchApi.api.get('uwe-entities/work-cases/'));
  },

  async getUWEEntity({ entityId, wfAccessToken }: { entityId: number; wfAccessToken: WFAccessToken }): Promise<DetailedUWEEntity> {
    const entity = await checkErrors(await fetchApi.api.get(`uwe-entities/${entityId}`, getAccessTokenHeaders(wfAccessToken)));

    return entity;
  },

  // Fetches the plaintext for a `uwe:mask: "reveal"` sensitive field. Only
  // works for fields configured that way — `masked`/`lastN` fields reject
  // this call server-side by design (they must never be revealable).
  async revealSensitiveField({
    entityId,
    fieldPath,
    wfAccessToken,
  }: {
    entityId: number | string;
    fieldPath: string;
    wfAccessToken?: WFAccessToken;
  }): Promise<{ value: string }> {
    return checkErrors(
      await fetchApi.api.get(`uwe-entities/${entityId}/fields/reveal`, {
        ...getAccessTokenHeaders(wfAccessToken as WFAccessToken),
        query: { path: fieldPath },
      })
    );
  },

  async getUWEEntityStatus({ entityId, wfAccessToken }: { entityId: number; wfAccessToken: WFAccessToken }) {
    return (await checkErrors(await fetchApi.api.get(`uwe-entities/${entityId}/status`, getAccessTokenHeaders(wfAccessToken)))).data;
  },

  async performActionOnUWEEntity({ id, action }: { id: number; action: string }) {
    return checkErrors(await fetchApi.api.post(`uwe-entities/${id}/perform-action/${action}`));
  },

  async getUWEEntityHistory({ id, wfAccessToken }: { id: number; wfAccessToken: WFAccessToken }) {
    return (await checkErrors(await fetchApi.api.get(`uwe-entities/${id}/history`, getAccessTokenHeaders(wfAccessToken)))).data;
  },

  async getWorkflowInfoForSlugForAnonymous({ slug, tenant }: { slug: string; tenant: number } = { slug: '', tenant: 0 }) {
    return (await checkErrors(await fetchApi.api.get(`anon/${tenant}/workflows/${slug}/info`))).data;
  },

  /**
   * The process info for a slug.
   *
   * `forCase` matters: with it, the server serves the definition THAT CASE is anchored to instead of
   * the published one. Viewing a case has to show the screens it was created under — without this,
   * two cases anchored to different versions rendered the same screen, and nothing looked wrong.
   * Omit it when creating a new case, which is exactly when the published definition IS the right one.
   */
  async getWorkflowInfoForSlug(
    { slug, forCase }: { slug: string; forCase?: number | string | null } = { slug: '' },
  ) {
    const q = forCase ? `?forCase=${encodeURIComponent(String(forCase))}` : '';
    return (await checkErrors(await fetchApi.api.get(`workflows/info/${slug}${q}`))).data;
  },

  /**
   * The process info by process id. Same endpoint as `getWorkflowInfoForSlug` — the server accepts
   * `workflow:{id}` in place of the slug — so `forCase` works here too, and matters for the same
   * reason: the case detail view reads `viewForm` off this, and a case must be shown the screens of
   * the version it is anchored to.
   */
  async getWorkflowInfoForId(
    { id, forCase }: { id: number; forCase?: number | string | null } = { id: 0 },
  ) {
    const q = forCase ? `?forCase=${encodeURIComponent(String(forCase))}` : '';
    return (await checkErrors(await fetchApi.api.get(`workflows/info/workflow:${id}${q}`))).data;
  },

  // The entry points of a workflow THIS caller may create an entity through, highest priority
  // first. A workflow can mark several steps `initial: true` so different roles start a case on a
  // different form, and eligibility depends on the step's role and permission preconditions — so
  // it is answered server-side (the same check the create call enforces) rather than filtered here.
  // Returns `[]` when the caller can't start this process at all; only a missing workflow errors.
  async getInitialStepsForSlug({ slug }: { slug: string } = { slug: '' }) {
    return (await checkErrors(await fetchApi.api.get(`workflows/${slug}/initial-steps`))).data;
  },

  async getInitialStepsForSlugForAnonymous({ slug, tenant }: { slug: string; tenant: number } = { slug: '', tenant: 0 }) {
    return (await checkErrors(await fetchApi.api.get(`anon/${tenant}/workflows/${slug}/initial-steps`))).data;
  },

  async getWorkflowStates({ workflowId, workflowName }: { workflowId?: number; workflowName?: string } = { workflowId: 0 }) {
    const query = { id: `workflow:${workflowName || workflowId}` };

    return (await checkErrors(await fetchApi.lookup.get('workflow-states', { query }))).data;
  },

  async getWorkflowStatesForEntity({ entityId }: { entityId: number } = { entityId: 0 }) {
    const query = { id: `entity:${entityId}` };

    return (await checkErrors(await fetchApi.lookup.get('workflow-states', { query }))).data;
  },

  async getWorkflowStatusMap({ id }: { id: number } = { id: 0 }) {
    const states = (await UWEEntityApi.getWorkflowStatesForEntity({ entityId: id })) || [];

    return states.reduce((_: Record<string, string>, { name, title }: any) => {
      _[name] = title || name;

      return _;
    }, {});
  },

  async getUWEEntityLog({ id, wfAccessToken }: { id: number; wfAccessToken: WFAccessToken }) {
    return checkErrors(await fetchApi.api.get(`uwe-entities/${id}/log`, getAccessTokenHeaders(wfAccessToken)));
  },

  async getUWEEntityProgressSteps({ entityId, fromParent, wfAccessToken }: { entityId: number; fromParent: boolean; wfAccessToken: WFAccessToken }) {
    const query = {
      fromParent: fromParent ? '1' : undefined,
    };

    return checkErrors(await fetchApi.api.get(`uwe-entities/${entityId}/progress-steps`, { ...getAccessTokenHeaders(wfAccessToken),query }));
  },

  async teleportUWEEntityToLogEntry({ id, stepIdx }: { id: number; stepIdx: number }) {
    return checkErrors(await fetchApi.api.post(`uwe-entities/${id}/log/teleport`, { data: { index: stepIdx } }));
  },

  async testDocumentExpression({ id, options }: { id: number; options: Object }) {
    return checkErrors(await fetchApi.api.put(`uwe-entities/${id}/test/document-expression`, { data: options }));
  },

  async testPreviewDocument({ id, options }: { id: number; options: Object }) {
    return checkErrors(await fetchApi.api.put(`uwe-entities/${id}/test/document-test`, { data: options }));
  },

  async testInternalNotificationMessage({ id, object }: { id: number; object: Object }) {
    return checkErrors(await fetchApi.api.put(`uwe-entities/${id}/test/internal-notification`, { data: object }));
  },

  async previewMetadata({ id, metadataObjectMap }: { id: number; metadataObjectMap: Object }) {
    return (await checkErrors(await fetchApi.api.post(`uwe-entities/${id}/preview-metadata`, { data: metadataObjectMap }))).data;
  },

  async getUWEEntityAssignments({ id, wfAccessToken }: { id: number; wfAccessToken: WFAccessToken }) {
    return checkErrors(await fetchApi.api.get(`uwe-entities/${id}/assignments`, getAccessTokenHeaders(wfAccessToken)));
  },

  async setUWEEntityAssignment({ id, roleId, userId, wfAccessToken }: { id: number; roleId: number; userId: number, wfAccessToken: WFAccessToken }) {
    return checkErrors(
      await fetchApi.api.post(`uwe-entities/${id}/assign`, {
        ...getAccessTokenHeaders(wfAccessToken),
        data: { roleId, userId },
      })
    );
  },

  async setEntityWorkflowStep({ id, step }: { id: number; step: Object }) {
    return checkErrors(
      await fetchApi.api.post(`uwe-entities/${id}/set-step`, {
        data: { step },
      })
    );
  },

  // `step` names WHICH initial step to create on. It is optional and only matters for a workflow
  // with several entry points: `page` alone can't disambiguate them (two initial steps may render
  // the same form), and omitting both lets the backend pick the highest-priority one the caller is
  // allowed to use.
  async createUWEEntity({ page, step, formData, wfAccessToken }: { page: string; step?: string; formData: Object; wfAccessToken: WFAccessToken }) {
    return checkErrors(await fetchApi.api.post('uwe-entities/', {...getAccessTokenHeaders(wfAccessToken), data: { ...formData, page, step, workflowAccessTokenId: wfAccessToken.id } }));
  },

  async updateUWEEntity({ id, page, formData, wfAccessToken }: { id: number; page: string; formData: Object; wfAccessToken: WFAccessToken }) {
    return checkErrors(await fetchApi.api.put(`uwe-entities/${id}/${page}`, { ...getAccessTokenHeaders(wfAccessToken),data: formData }));
  },

    async createUWEEntityAsAnonymous({
    payload: { page, step, formData },
    slug,
    tenant,
  }: {
    payload: { id: number; page: string; step?: string; formData: Object };
    slug: string;
    tenant: number;
  }) {
    return checkErrors(await fetchApi.api.post(`anon/${tenant}/workflows/${slug}/uwe-entities/new`, { data: { ...formData, page, step } }));
  },

    async updateUWEEntityAsAnonymous({
    payload: { id, page, formData },
    slug,
    tenant,
  }: {
    payload: { id: number; page: string; formData: Object };
    slug: string;
    tenant: number;
  }) {
    return checkErrors(await fetchApi.api.put(`anon/${tenant}/workflow/${slug}/uwe-entities/${id}/${page}`, { data: formData }));
  },

  async addProducts({ id, productIds }: { id: number; productIds: Object }) {
    return checkErrors(await fetchApi.api.post(`uwe-entities/${id}/products`, { data: { productIds } }));
  },

  async awaitDocuments({ id, page, wfAccessToken }: { id: number; page: string; wfAccessToken: WFAccessToken }) {
    return checkErrors(
      await fetchApi.api.put(`uwe-entities/${id}/await-documents`, {
        ...getAccessTokenHeaders(wfAccessToken),
        data: {
          page,
        },
      })
    );
  },

  async createUWEEntityNote({
    entityId,
    note,
    wfAccessToken,
    notifyConfig,
  }: {
    entityId: number;
    note: string;
    wfAccessToken: WFAccessToken;
    // Optional "notify assignees" config, sourced from the submitting field's own uiSchema (e.g.
    // NotesField's `ui:notifyOnAdd`) — not a workflow-level setting. Same shape as the `notification`
    // WFStep's own fields: { service: string | string[], channels?: string[] }. Omitted entirely means
    // "don't notify anyone", which is the default for every other note-posting surface (e.g. the
    // sidebar's UWEEntityNote panel, which doesn't pass this).
    notifyConfig?: Record<string, unknown>;
  }) {
    return checkErrors(
      await fetchApi.api.post(`uwe-entities/${entityId}/notes`, {...getAccessTokenHeaders(wfAccessToken),
        data: { note, notifyConfig },
        json: true,
      })
    );
  },

  async getUWEEntityNotes({ entityId, wfAccessToken }: { entityId: number; wfAccessToken: WFAccessToken }) {
    return checkErrors(await fetchApi.api.get(`uwe-entities/${entityId}/notes`, getAccessTokenHeaders(wfAccessToken)));
  },

  async getRoleAssignments({ id, wfAccessToken }: { id: number; wfAccessToken: WFAccessToken }) {
    return checkErrors(await fetchApi.api.get(`uwe-entities/${id}/role-assignments`, getAccessTokenHeaders(wfAccessToken)));
  },

  async getUWEEntityZipDownloadUrl({ id, filter, wfAccessToken }: { id: number; filter: Object; wfAccessToken: WFAccessToken }) {
    return (await checkErrors(await fetchApi.api.get(`uwe-entities/${id}/download-documents`, { ...getAccessTokenHeaders(wfAccessToken), query: { filter } }))).data;
  },

  async getWorkflowRoles({ id, wfAccessToken }: { id: number; wfAccessToken: WFAccessToken }) {
    return checkErrors(await fetchApi.api.get(`uwe-entities/${id}/workflow-roles`, getAccessTokenHeaders(wfAccessToken)));
  },

  async getProgressByOrganization({ id }: { id: number }) {
    return checkErrors(await fetchApi.api.get(`uwe-entities/getProgressByOrganization/${id}`));
  },

  async generatePreviewFor({ id, data }: { id: number; data: Object }) {
    return (
      await checkErrors(
        await fetchApi.api.post(`uwe-entities/${id || 0}/form-preview`, {
          data,
          json: true,
        })
      )
    ).data;
  },

  async verifyPreviewGeneration({ referenceId }: { referenceId: number }) {
    const response = checkDocumentResponse((await checkErrors(await fetchApi.api.get(`uwe-entities/form-preview/verify/${referenceId}`))).data);

    if (response.status === STATUS.NOTFOUND || response.status === STATUS.ERROR) {
      throw new Error('Error buscando documento.');
    }

    return response;
  },

  async submissionFromEmail({ token }: { token: string }) {
    return (await checkErrors(await fetchApi.api.get(`uwe-entities/submissionFromEmail/${token}`))).data;
  },

  async createWfAccessTokenHash({
    workflowId,
    uweEntityId,
    canView,
    canCreate,
    canUpdate,
  }: {
    workflowId?: number | null;
    uweEntityId?: number;
    canView?: boolean;
    canCreate?: boolean;
    canUpdate?: boolean;
  }) {
    return checkErrors(
      await fetchApi.api.post('uwe-entities/workflow-access-token', {
        data: { workflowId, uweEntityId, canView, canCreate, canUpdate },
      })
    );
  },
};

export default UWEEntityApi;
